import {Typography} from "@mui/material";
import {
	DataTable,
	DataTableColumn,
	SwitchableTemporalFormat,
	tryParseInstant,
	useDataTableStorage,
	useSpringPage,
	useSpringPageable,
	useStorage,
} from "@variocube/app-ui";
import React, {useMemo} from "react";
import {useAsync} from "react-async-hook";
import {AuditLogEntry, useTenantApi} from "../../api";
import {EmptyList} from "../../controls";
import {HasKeyFunc, TFunc, useLocalization, useWordingFlavor} from "@smalox/shared";
import {WordingFlavor} from "@smalox/shared";
import {useTenantId} from "../../tenant";
import {View, ViewHeader} from "../View";
import {ActorChip} from "./ActorChip";
import {AuditLogEntryDetailsButton} from "./AuditLogEntryDetailsButton";
import {AuditLogFilter} from "./AuditLogFilter";

const defaultFilter: AuditLogFilter = {};

export function AuditLog() {
	const {t, hasKey} = useLocalization();
	const {queryTenantAuditLog} = useTenantApi();
	const tenantId = useTenantId();
	const flavor = useWordingFlavor();

	const columns = useMemo(() => createColumns(t, hasKey, flavor), [t, hasKey, flavor]);

	const {onPageChange, onSort, ...storage} = useDataTableStorage("AuditLogList.paging");
	const pageable = useSpringPageable(storage);

	const [filter, setFilter] = useStorage<AuditLogFilter>("AuditLogList.filter", defaultFilter);

	const query = useMemo(() => ({
		...filter,
		...pageable,
	}), [filter, pageable]);

	const {loading, error, result} = useAsync(queryTenantAuditLog, [tenantId, query]);
	const {rows, page} = useSpringPage(result);

	return (
		<View>
			<ViewHeader
				title={t("audit.title")}
			/>
			<AuditLogFilter value={filter} onChange={setFilter} />
			<DataTable
				columns={columns}
				rows={rows}
				page={page}
				loading={loading}
				error={error}
				onSort={onSort}
				onPageChange={onPageChange}
				empty={<EmptyList />}
				{...storage}
			/>
		</View>
	);
}

function createColumns(t: TFunc, hasKey: HasKeyFunc, flavor: WordingFlavor): DataTableColumn<AuditLogEntry>[] {
	return [
		{
			label: t("audit.id"),
			field: "id",
			sortable: true,
			default: true,
			component: ({row}) => (
				<Typography fontFamily="JetBrains Mono" fontSize="large">
					{row.id}
				</Typography>
			),
		},
		{
			label: t("audit.eventTimestamp"),
			field: "eventTimestamp",
			sortable: true,
			default: true,
			component: ({row}) => <SwitchableTemporalFormat value={tryParseInstant(row.eventTimestamp)} />,
		},
		{
			label: t("audit.actor"),
			field: "actor",
			sortable: true,
			default: true,
			component: ({row}) => <ActorChip actor={row.actor} />,
		},
		{
			label: t("audit.action"),
			field: "action",
			sortable: true,
			default: true,
			component: ({row}) => {
				const key = `audit.actions.${row.action}.${flavor}`;
				return <Typography>{hasKey(key) ? t(key) : t(`audit.actions.${row.action}` as any)}</Typography>;
			},
		},
		{
			label: t("audit.showDetails"),
			field: "",
			sortable: false,
			default: true,
			component: ({row}) => <AuditLogEntryDetailsButton entry={row} />,
		},
	];
}
